import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { colors } from "../theme";
import Header from "../components/Header";
import SwipeTrack from "../components/SwipeTrack";
import api from "../api";
import { cleanDescription } from "../utils/cleanDescription";
import { formatTimeLeft as sharedFormatTimeLeft } from "../hooks/useBets";

interface BetData {
  id: number;
  bet_id: string;
  description: string;
  amount: number; // nanotons
  status: string;
  creator_telegram_id: number;
  challenger_name: string;
  creator_name?: string;
  creator_username?: string;
  opponent_telegram_id: number;
  opponent_name: string;
  expires_at: string;
  strava_bet_type: string | null;
  strava_target_value: number | null;
  category?: string;
}

export default function AcceptPage() {
  const { betId } = useParams<{ betId: string }>();
  const navigate = useNavigate();
  const [tonConnectUI] = useTonConnectUI();

  const [bet, setBet] = useState<BetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tonSent, setTonSent] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tonPrice, setTonPrice] = useState<number | null>(null);

  // Fetch bet data
  useEffect(() => {
    if (!betId) {
      setLoading(false);
      setLoadError("No bet ID");
      return;
    }

    let cancelled = false;

    async function loadBet() {
      try {
        const res = await api.get(`/bets/${betId}`);
        if (cancelled) return;

        // If bet is already accepted (not funded), redirect to detail
        if (res.data.status && res.data.status !== "funded") {
          navigate(`/bet/${betId}`, { replace: true });
          return;
        }

        setBet(res.data);
      } catch (err: any) {
        if (cancelled) return;
        // If 401, token may not be ready yet — retry once after 1s
        if (err.response?.status === 401) {
          await new Promise(r => setTimeout(r, 1500));
          try {
            const retry = await api.get(`/bets/${betId}`);
            if (cancelled) return;
            if (retry.data.status && retry.data.status !== "funded") {
              navigate(`/bet/${betId}`, { replace: true });
              return;
            }
            setBet(retry.data);
          } catch (retryErr: any) {
            if (!cancelled) setLoadError("Failed to load bet");
          }
        } else {
          setLoadError(err.response?.data?.detail || "Failed to load bet");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadBet();
    return () => { cancelled = true; };
  }, [betId, navigate]);

  // Fetch TON price
  useEffect(() => {
    api
      .get("/crypto/price/toncoin")
      .then((res) => {
        const price = res.data?.price || res.data?.toncoin?.usd;
        if (typeof price === "number") setTonPrice(price);
      })
      .catch(() => setTonPrice(1.34));
  }, []);

  // VEN-38: tick the countdown every second so Accept shows live hh/mm/ss.
  const [timeLeft, setTimeLeft] = useState(
    bet ? formatTimeLeft(bet.expires_at) : "",
  );
  useEffect(() => {
    if (!bet) return;
    setTimeLeft(formatTimeLeft(bet.expires_at));
    const id = setInterval(
      () => setTimeLeft(formatTimeLeft(bet.expires_at)),
      1_000,
    );
    return () => clearInterval(id);
  }, [bet?.expires_at]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Header title="ACCEPT" showClose />
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.grayLight,
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  if (loadError || !bet) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Header title="ACCEPT" showClose />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>{"\u26A0\uFE0F"}</div>
          <div style={{ fontSize: 16, color: colors.danger, marginBottom: 8 }}>
            {loadError || "Bet not found"}
          </div>
          <div style={{ fontSize: 14, color: colors.grayLight }}>
            This bet may have expired or been cancelled
          </div>
        </div>
      </div>
    );
  }

  const amountTon = bet.amount / 1_000_000_000;
  const amountDisplay = amountTon % 1 === 0 ? amountTon.toFixed(1) : amountTon.toString();
  const fiatAmount = tonPrice ? (amountTon * tonPrice).toFixed(2) : (amountTon * 1.34).toFixed(2);
  const potTon = amountTon * 2;
  const potDisplay = potTon % 1 === 0 ? potTon.toFixed(1) : potTon.toString();
  const isFitness = bet.strava_bet_type !== null;
  const creatorName = (bet.challenger_name || bet.creator_name || "Someone").replace(/^@/, "");
  const rawName = bet.creator_username || bet.challenger_name || "user";
  const creatorUsername = rawName.replace(/^@/, "");
  const cleanedDescription = cleanDescription(bet.description);

  const handleSwipeComplete = async () => {
    if (tonSent) return;

    // Pre-flight: wallet must be connected
    if (!tonConnectUI.account) {
      setError(null);
      try {
        await tonConnectUI.openModal();
      } catch {}
      if (tonConnectUI.account) {
        setError("Wallet connected \u2014 swipe again to accept");
      } else {
        setError("Connect your TON wallet to accept the bet");
      }
      return;
    }

    setTonSent(true);
    setProcessing(true);
    setError(null);

    try {
      // 1. Get escrow address
      const escrowRes = await api.get("/bets/escrow-address");
      // VEN-64: user-friendly form — raw `0:<hex>` blocks some Tonkeeper
      // builds from opening the wallet sheet at all (reverts VEN-21).
      const escrowAddress = escrowRes.data?.address || escrowRes.data;

      // 2. Send TON
      const tx = {
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [
          {
            address: escrowAddress,
            amount: bet.amount.toString(),
          },
        ],
      };

      // Save wallet
      const wallet = tonConnectUI.account;
      if (wallet?.address) {
        try {
          await api.put("/users/me/wallet", { ton_address: wallet.address, ton_address_raw: wallet.address });
        } catch {}
      }

      await tonConnectUI.sendTransaction(tx);

      // Retry deposit verification
      for (let attempt = 0; attempt < 6; attempt++) {
        try {
          await api.post(`/bets/${bet.bet_id}/accept`);
          break;
        } catch (e: any) {
          if (e.response?.status === 400 && e.response?.data?.detail?.includes("not confirmed") && attempt < 5) {
            await new Promise(r => setTimeout(r, 8000));
            continue;
          }
          throw e;
        }
      }

      setSuccess(true);
      setProcessing(false);

      setTimeout(() => navigate(`/bet/${bet.bet_id}`), 1500);
    } catch (err) {
      setProcessing(false);
      const msg = err instanceof Error ? err.message : String(err);
      const backendDetail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

      if (msg.includes("TON_CONNECT_SDK_ERROR") || /connect.*wallet/i.test(msg)) {
        setTonSent(false);
        setError("Connect your TON wallet to accept the bet");
      } else if (msg.includes("cancel") || msg.includes("reject") || msg.includes("closed")) {
        setTonSent(false);
        setError("Wallet cancelled \u2014 swipe to retry");
      } else if (backendDetail?.includes("not confirmed")) {
        setError("Deposit is taking longer than usual to confirm. Your funds are safe \u2014 please wait a minute and tap the home button to check your bets.");
      } else if (backendDetail) {
        setError(backendDetail);
      } else {
        setError(msg || "Something went wrong");
      }
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Header title="ACCEPT" showClose onClose={() => navigate("/")} />

      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "0 16px 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Challenge header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{"\u26A1"}</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>You've been CHALLENGED</div>
        </div>

        {/* Bet card */}
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: 16,
            marginBottom: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                background: colors.purple,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                fontWeight: 700,
                color: colors.white,
              }}
            >
              {creatorName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{creatorName}</div>
              <div style={{ fontSize: 13, color: colors.grayLight }}>@{creatorUsername}</div>
            </div>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{cleanedDescription}</div>
          <div style={{ display: "flex", gap: 12, fontSize: 13, color: colors.grayLight }}>
            <span>{isFitness ? "HEALTHBRIDGE AUTO \u2713" : "BOTH AGREE"}</span>
            <span>{"\u23F0"} {timeLeft}</span>
            <span>{"\uD83D\uDCB0"} {amountDisplay} TON</span>
          </div>
        </div>

        {/* Match it + hero amount */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: colors.grayLight, textTransform: "uppercase", marginBottom: 8 }}>
            Match it
          </div>
          <div style={{ fontSize: 48, fontWeight: 800, color: colors.primary, lineHeight: 1.1 }}>
            {amountDisplay} TON
          </div>
          <div style={{ fontSize: 15, color: colors.grayLight, marginTop: 4 }}>
            {"\u2248"} ${fiatAmount}
          </div>
        </div>

        {/* Pot info */}
        <div style={{ textAlign: "center", fontSize: 13, color: colors.grayLight, marginTop: 16, marginBottom: 32 }}>
          Total pot: {potDisplay} TON {"\u00B7"} Winner takes all
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Swipe track */}
        <SwipeTrack
          label={`LOCK IN \u2014 ${amountDisplay} TON`}
          onComplete={handleSwipeComplete}
          processing={processing}
          success={success}
          error={error}
        />

        <div
          style={{
            textAlign: "center",
            fontSize: 13,
            color: colors.grayLight,
            marginTop: 12,
            paddingBottom: 8,
          }}
        >
          Think they'll fail? Easy money. {"\uD83D\uDE0F"}
        </div>
      </div>
    </div>
  );
}

// VEN-38: delegate to the shared util so Accept matches Home/BetDetail.
function formatTimeLeft(expiresAt: string): string {
  return sharedFormatTimeLeft(expiresAt);
}
